import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark, Check, ExternalLink, Loader2, Trash2, Sparkles, CircleCheck, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { SEARCH_SITES, searchQuery, amazonBuyUrl } from "@/lib/searchLinks";
import { api, formatApiError } from "@/lib/api";

const FILTERS = { wanted: "Wanted", acquired: "Got it", all: "All" };

const WishRow = ({ w, onToggle, onDelete }) => {
  const q = searchQuery(w.title, w.artist === "Unknown artist" ? "" : w.artist);
  return (
    <div data-testid={`wishlist-item-${w.id}`} className="group flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-zinc-50/70 sm:flex-row sm:items-center">
      <button onClick={() => onToggle(w)} data-testid={`wishlist-toggle-${w.id}`} title={w.acquired ? "Mark as wanted" : "Mark as acquired"}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${w.acquired ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-300 text-transparent hover:border-zinc-900 hover:text-zinc-400"}`}>
        <Check size={13} strokeWidth={3} />
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${w.acquired ? "text-zinc-400 line-through" : ""}`}>
          {w.title} <span className="font-normal text-zinc-500">— {w.artist}</span>
          {w.year ? <span className="font-mono text-xs text-zinc-400"> · {w.year}</span> : null}
        </p>
        {w.reason && <p className="mt-0.5 text-xs text-zinc-500">{w.reason}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {w.source && <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">from {w.source}</span>}
          {w.in_library && <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700" data-testid={`wishlist-in-library-${w.id}`}><CircleCheck size={10} />In your library</span>}
          {w.acquired_auto && <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700" data-testid={`wishlist-auto-found-${w.id}`}><Sparkles size={10} />Found automatically{w.acquired_at ? ` · ${new Date(w.acquired_at).toLocaleDateString()}` : ""}</span>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {!w.acquired && (
          <a href={amazonBuyUrl(q)} target="_blank" rel="noopener noreferrer" data-testid={`wishlist-buy-amazon-${w.id}`}
            title="Buy the MP3 on Amazon and keep the file"
            className="flex items-center gap-1 rounded-full bg-amber-600 px-3 py-1 text-[11px] font-medium text-white transition-colors hover:bg-amber-700">
            <ShoppingCart size={11} />Buy on Amazon
          </a>
        )}
        {SEARCH_SITES.map((s) => (
          <a key={s.id} href={s.url(q)} target="_blank" rel="noopener noreferrer" data-testid={`wishlist-search-${s.id}-${w.id}`}
            className="flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11px] text-zinc-600 transition-colors hover:border-zinc-900 hover:text-zinc-900">
            {s.label}<ExternalLink size={10} />
          </a>
        ))}
        <button onClick={() => onDelete(w)} data-testid={`wishlist-delete-${w.id}`} className="ml-1 rounded p-1.5 text-zinc-300 transition-colors hover:text-red-600"><Trash2 size={14} /></button>
      </div>
    </div>
  );
};

export default function Wishlist() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("wanted");
  const navigate = useNavigate();
  useEffect(() => { api.get("/wishlist").then((r) => setItems(r.data)); }, []);
  const shown = useMemo(() => (items || []).filter((w) => filter === "all" || (filter === "acquired" ? w.acquired : !w.acquired)), [items, filter]);
  if (!items) return <Loader2 className="animate-spin text-zinc-400" />;

  const toggle = async (w) => {
    try {
      const { data } = await api.patch(`/wishlist/${w.id}`, { acquired: !w.acquired });
      setItems(items.map((x) => (x.id === w.id ? { ...x, acquired: data.acquired } : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (w) => {
    await api.delete(`/wishlist/${w.id}`);
    setItems(items.filter((x) => x.id !== w.id));
  };
  const wanted = items.filter((w) => !w.acquired).length;

  return (
    <div>
      <PageHeader eyebrow={`${wanted} wanted · ${items.length - wanted} acquired`} title="Wishlist" testid="wishlist-title">
        {shown.length > 0 && (
          <button onClick={() => navigate("/builder", { state: { wishlist: shown.slice(0, 60) } })} data-testid="wishlist-build-mix-button"
            className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white transition-transform hover:scale-[1.03] active:scale-95">
            <Sparkles size={14} className="text-amber-400" />Build a mix from {filter === "all" ? "all" : FILTERS[filter].toLowerCase()} ({Math.min(shown.length, 60)})
          </button>
        )}
        <div className="flex rounded-full border border-zinc-200 bg-white p-1">
          {Object.entries(FILTERS).map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`wishlist-filter-${k}`}
              className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${filter === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}>{v}</button>
          ))}
        </div>
      </PageHeader>
      {items.length === 0 ? (
        <EmptyState icon={Bookmark} title="Nothing on your wishlist" text="Build a playlist and save the discovery picks you want to track down."
          action={<Link to="/builder" data-testid="wishlist-builder-link" className="flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2 text-sm text-white"><Sparkles size={14} className="text-amber-400" />Open Playlist Builder</Link>} />
      ) : shown.length === 0 ? (
        <p className="py-10 text-sm text-zinc-500" data-testid="wishlist-filter-empty">No items in this view.</p>
      ) : (
        <div className="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white" data-testid="wishlist-list">
          {shown.map((w) => <WishRow key={w.id} w={w} onToggle={toggle} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
